// PersonDetailModal — shared "who is this person, what can they do, why" surface.
//
// §unified-perms PR #8 (2026-05-23). Opens by writing the person object to
// `root._personDetail`. Reads canonical state already loaded by the shell:
//   • root.teams           — full list with category + permissions[]
//   • root.teamsByEmail    — { [email]: [{ id, slug, role, ... }] }
//   • root.orgMembersByOrgId[orgId] — { [email]: { role } | true } (legacy = true)
//   • root.jwtClaims       — for action gating via hasPerm
//
// The same modal opens from /admin row click (this PR) and later from
// /team-and-agents node click (PR #9). Effective-permission math mirrors
// `server/src/core/utils/effectivePermissions.js unionEffectivePermissions`:
// owner short-circuits; otherwise org-role baseline ∪ per-team permissions,
// with passive memberships intersected against CATEGORY_PERMISSION_TEMPLATES.passive.

// Only `effectiveCategory` remains in scope — used by the IdentityRow's
// CategoryBadge to label the person's overall team category. The other
// permission helpers (PERMISSION_KEYS, PERMISSIONS_BY_GROUP, permLabel,
// hasPerm, etc.) were removed when the permission UI was stripped
// 2026-06-03.
import { effectiveCategory } from '../functions/permissions.js'

const _detailPerson = (root) => root?._personDetail || null

const _activeOrgId = (root) => root?.jwtClaims?.workspace_org_id || null

// Org role for this person in the active org. Searches three sources
// in priority order:
//   1. `root.roles.byUser[userId]` — the authoritative roles map filled
//      in by loadPrivateData._loadRoles via sdk.listOrgMembers. This
//      is the only source that distinguishes owner / admin / member.
//   2. `orgMembersByOrgId[orgId][email]` as an object `{ role }` —
//      future-proof shape; not currently written but handled if/when it
//      lands.
//   3. Membership existence in `orgMembersByOrgId` — falls through to
//      'member' (the legacy boolean-`true` shape is just a presence
//      marker, never carried a role).
//
// Before this rewrite the function only handled shape (2) and returned
// 'member' for the boolean shape — meaning any OTHER person who was
// actually the org owner appeared as a plain member in the modal's
// Effective Permissions panel.
const _orgRoleFor = (root, email) => {
  if (!email) return 'member'
  const norm = String(email).toLowerCase()
  const person = (root?.people || []).find((p) => String(p?.email || '').toLowerCase() === norm)
  if (person?.userId && root?.roles?.byUser?.[person.userId]) {
    return String(root.roles.byUser[person.userId]).toLowerCase()
  }
  const orgId = _activeOrgId(root)
  const entry = orgId && root?.orgMembersByOrgId?.[orgId]?.[norm]
  if (entry && typeof entry === 'object' && entry.role) {
    return String(entry.role).toLowerCase()
  }
  return 'member'
}

// Resolve the canonical team rows for a person — joins `teamsByEmail[email]`
// against `root.teams` so we get category + permissions[] (the per-email
// index only carries identity, not the perms).
const _teamsFor = (root, personOrEmail) => {
  // Accept a person-like { userId, email } (preferred) or a bare email.
  // id-first: a non-mgmt viewer's rows carry no email (server projection), so
  // resolve via the stable userId before the email fallback (fable.md P0).
  const p = typeof personOrEmail === 'string' ? { email: personOrEmail } : personOrEmail || {}
  const uid = p.userId != null ? String(p.userId) : ''
  const email = String(p.email || '').toLowerCase()
  if (!uid && !email) return []
  const byUid = (uid && root?.teamsByUserId?.[uid]) || null
  const memberships = (byUid && byUid.length ? byUid : email && root?.teamsByEmail?.[email]) || []
  const byId = new Map((root?.teams || []).map((t) => [t.id, t]))
  return memberships.map((m) => {
    const full = byId.get(m.id) || {}
    return {
      id: m.id,
      name: m.name || full.name || '',
      slug: m.slug || full.slug || '',
      orgId: m.orgId || full.orgId || null,
      role: m.role || 'member',
      // `status` lives on TeamMember (per-member overlay, PR #1). Until the
      // workspace data layer threads it through teamsByEmail, infer from
      // the team's category — passive teams contribute only passive perms.
      status: m.status || (full.category === 'passive' ? 'passive' : 'active'),
      category: full.category || 'active',
      permissions: Array.isArray(full.permissions) ? full.permissions : []
    }
  })
}

// (Removed 2026-06-03: _computeEffective — owner short-circuit + org
// role baseline ∪ team perms math powered the EffectiveSection panel.
// Server-side unionEffectivePermissions is still authoritative; the
// client mirror retires with the UI.)

const _categoryColor = (cat) => {
  if (cat === 'manager') return 'purple'
  if (cat === 'active') return 'blue'
  if (cat === 'passive') return 'gray'
  if (cat === 'external') return 'orange'
  if (cat === 'archived') return 'red'
  return 'gray'
}

// Every key is a LITERAL — a key built by template is invisible to grep, to
// the parity test and to every extraction tool (docs/I18N-CONVENTION.md §1.3).
const CATEGORY_KEY = {
  manager: 'workspace.people.category.manager',
  active: 'workspace.people.filter.active',
  passive: 'workspace.people.category.passive',
  external: 'workspace.people.category.external',
  archived: 'workspace.people.category.archived'
}
const _categoryLabel = (el, cat) =>
  el.call('polyglot', CATEGORY_KEY[cat] || 'workspace.person.categoryUnassigned')

// (Removed 2026-06-03: _orgRoleRank + _alreadyMaintainerOrAbove +
// _navigateTeams. They gated the Promote-to-Manager button + Manage-
// Teams shortcut, both stripped with the rest of the admin UI.)

// Close the person-detail modal layer via the registered windows API
// helper. Pre-migration this set `_personDetail: null` directly; now
// closePersonDetailModal handles state + layer pop.
const _closePersonDetail = (el) => el.call('closePersonDetailModal')

const _navigateProfile = (el, root) => {
  const p = _detailPerson(root)
  if (!p?.email) return
  _closePersonDetail(el)
  const routeRoot = el.call('findRouteRoot', el)
  if (routeRoot) el.router(el.call('profilePath', p.email), routeRoot)
}

// Body-only component — backdrop, ESC binding, dismiss-on-backdrop are
// owned by @symbo.ls/windows ModalLayer. Triggered via
// `el.call('openPersonDetailModal', person)` and closed via
// `el.call('closePersonDetailModal')` — helpers in workspace/functions/.
// `root._personDetail` + the `_personDetailShowAdd` /
// `_personDetailAddTeamId` siblings retained as data carriers.
export const PersonDetailModal = {
  // WS-MODAL-X-HORIZONTAL-OVERFLOW-1: fill the custom-template slot rather
  // than forcing the 'I' width inside it — same class as AppCreateOrgModal's
  // fixed-'H'-in-a-narrower-slot bug. The window width now comes from the
  // `openPersonDetailModal` layer descriptor; a body wider than the slot
  // overflowed the window (`overflow: 'visible'`) and pushed the ModalHeader
  // Close X outside both the window and the viewport.
  width: '100%',
  maxWidth: '100%',
  maxHeight: '85vh',
  // A COLUMN whose MIDDLE scrolls (Body, below): the head and the footer stay
  // pinned, and nothing on this root clips. The brand ModalHeader pulls its
  // Close X into the window's padding with a negative margin; an `overflow`
  // on this root cut that X by 12px and the footer's Open profile by 4px
  // (live on dev 2026-09-26, 1440 x 813).
  flow: 'y',
  minWidth: '0',

  ModalHeader: {
    // WS-MODAL-X-HORIZONTAL-OVERFLOW-1 (local override, brand chrome stays
    // untouched): the shared ModalHeader gives Hgroup `flex: 1` with no
    // `minWidth`, and its `P` a fixed `maxWidth: 'I_default'`. A long email
    // therefore sets a min-content floor for the title block, the row cannot
    // shrink to a narrow window, and the Close X — last item in the row — is
    // the first thing pushed out of the window. `minWidth: '0'` + a
    // percentage cap + `overflowWrap` let the block shrink instead.
    Hgroup: {
      minWidth: '0',
      H: {
        overflowWrap: 'anywhere',
        text: (el, s) =>
          _detailPerson(s.root)?.name ||
          _detailPerson(s.root)?.email ||
          el.call('polyglot', 'workspace.person.fallbackName')
      },
      P: {
        maxWidth: '100%',
        overflowWrap: 'anywhere',
        text: (el, s) => _detailPerson(s.root)?.email || ''
      }
    },
    // Override the brand's inherited Close — dispatch the windows-layer
    // close instead of the stateKey walker.
    Icons: {
      Close: {
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('closePersonDetailModal')
        }
      }
    }
  },

  Body: {
    flow: 'y',
    gap: 'B',
    padding: 'A B B B',
    // THE scroll region of the sheet (the root no longer clips).
    flex: '1 1 auto',
    minHeight: '0',
    overflowY: 'auto',
    overscrollBehavior: 'contain',

    // ── Identity row ──
    // Straight on the sheet (DESIGN_SPEC §6.2, the no-wrapper law) — it was a
    // box on an `overlay` tint inside the rounded window. The avatar is the
    // fixed `avatarMd` circle.
    IdentityRow: {
      flow: 'x',
      align: 'center',
      gap: 'A',

      Avatar: {
        align: 'center center',
        boxSize: 'avatarMd',
        round: 'radiusPill',
        src: (el, s) => el.call('avatarUrlFor', _detailPerson(s.root)?.email)
      },

      Info: {
        flow: 'y',
        gap: 'V',
        flex: '1',
        minWidth: '0',

        Name: {
          tag: 'span',
          fontSize: 'A',
          fontWeight: '700',
          color: 'title',
          text: (el, s) => _detailPerson(s.root)?.name || '—'
        },

        Sub: {
          tag: 'div',
          flow: 'x',
          align: 'center',
          gap: 'Z',
          fontSize: 'Z2',
          color: 'caption',

          Role: {
            text: (el, s) =>
              el
                .call('polyglot', 'workspace.person.orgRole')
                .replace('{role}', _orgRoleFor(s.root, _detailPerson(s.root)?.email))
          },
          Dot: { text: '·' },
          Category: {
            text: (el, s) => {
              const cat = effectiveCategory(_teamsFor(s.root, _detailPerson(s.root)))
              return _categoryLabel(el, cat)
            }
          }
        }
      },

      // The category is a STATUS: a tint of its hue under the neutral title
      // ink (DESIGN_LANGUAGE §4.2 / §8.5 rule 3 — wash + neutral ink). It was
      // white on the raw hue: 3.6:1 on blue, 3.2:1 on red, under the 4.5 floor
      // in both schemes.
      CategoryBadge: {
        tag: 'span',
        padding: 'V Z1',
        round: 'radiusPill',
        fontSize: 'Z2',
        fontWeight: '500',
        whiteSpace: 'nowrap',
        color: 'title',
        background: (el, s) =>
          _categoryColor(effectiveCategory(_teamsFor(s.root, _detailPerson(s.root)))) + '.12',
        text: (el, s) => _categoryLabel(el, effectiveCategory(_teamsFor(s.root, _detailPerson(s.root))))
      }
    },

    // ── Teams section ──
    TeamsSection: {
      flow: 'y',
      gap: 'Z',

      // The section head is the kit's mono micro-label (a heading element).
      Heading: {
        extends: 'AppMicroLabel',
        tag: 'h3',
        margin: '0',
        text: (el, s) => {
          const n = _teamsFor(s.root, _detailPerson(s.root)).length
          return el.call('polyglot', 'workspace.personModal.teams').replace('{count}', String(n))
        }
      },

      Empty: {
        hide: (el, s) => _teamsFor(s.root, _detailPerson(s.root)).length > 0,
        padding: 'Z A',
        fontSize: 'Z1',
        color: 'caption',
        fontStyle: 'italic',
        text: '{{ workspace.personModal.noTeams | polyglot }}'
      },

      // Teams list — read-only after 2026-06-03 simplification. Each
      // row shows team name + category pill. Per-member perms count,
      // active/passive toggle and Remove-from-team button were
      // stripped per Nika's "no permission UI in user profile" ask;
      // team admin actions live at /team-and-agents and /admin.
      List: {
        flow: 'y',
        gap: 'V',
        childExtends: {
          flow: 'x',
          align: 'center',
          gap: 'Z',
          // Flat rows on the sheet — no `overlay` box per row (§6.2); the
          // list's own gap separates them.
          padding: 'Y 0',

          TeamName: {
            tag: 'span',
            flex: '1',
            minWidth: '0',
            fontSize: 'Z1',
            fontWeight: '700',
            color: 'title',
            text: (el, s) => s.name
          },

          // Same status treatment as the CategoryBadge above.
          CategoryPill: {
            tag: 'span',
            padding: 'V Z',
            round: 'radiusPill',
            fontSize: 'Y2',
            fontWeight: '500',
            whiteSpace: 'nowrap',
            color: 'title',
            background: (el, s) => _categoryColor(s.category) + '.12',
            text: (el, s) => _categoryLabel(el, s.category)
          }
        },
        childrenAs: 'state',
        children: (el, s) => _teamsFor(s.root, _detailPerson(s.root))
      }

      // (Removed 2026-06-03: AddPanel — inline Add-to-Team picker.
      // Permission management surfaces live at /admin and
      // /team-and-agents; the user-profile modal is read-only now.)
    }

    // (Removed 2026-06-03: EffectiveSection — the Effective
    // Permissions panel listed every individual perm grouped by
    // category with attribution lines. Removed per Nika's "remove
    // permissions in modal" ask — see git history for the prior
    // shape if it needs to come back as a separate /profile/perms
    // surface.)
  },

  // The kit footer (DESIGN_SPEC §6.9): the quiet dismiss is the ghost
  // capsule, the one action — Open profile — is the ink primary, last. (It
  // was the reverse: a filled "Close" as the primary and the real action as
  // the ghost.)
  ModalFooter: {
    flow: 'x',
    gap: 'Z1',
    align: 'center end',

    // (Removed 2026-06-03: AddToTeamBtn, ManageTeamsBtn,
    // PromoteToManagerBtn — every permission/admin action button in
    // the footer is gone. Profile modal is read-only now; admin
    // surfaces own promote/add/manage actions.)

    CloseBtn: {
      extends: 'AppCapsuleBtnGhost',
      text: '{{ workspace.personModal.close | polyglot }}',
      onClick: (e, el) => _closePersonDetail(el)
    },

    OpenProfileBtn: {
      extends: 'AppCapsuleBtnPrimary',
      text: '{{ workspace.personModal.openProfile | polyglot }}',
      hide: (el, s) => !_detailPerson(s.root)?.email,
      onClick: (e, el, s) => _navigateProfile(el, s.root)
    }
  }
}
